
/**
 * Visual menu switcher component.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-05-24 (1.0.0) (jhoward)
 *    - <menu-switcher> facilitates quickly changing menus within the set of menus passed in as "menus" prop.
 *
 *  2022-10-05 (1.0.1) (jhoward)
 *    - Fix bug in layout of <menu-switcher> where the closed viewport would still overlay much of the page.
 *
 *  2023-03-24 (1.1.0) (jhoward)
 *    - Add omnipresent version of <menu-switcher> with menu icons.
 *
 *  2023-06-19 (1.1.1) (jhoward)
 *    - Make background partially see-through in <menu-switcher>.
 *
 *  2023-07-26 (1.1.2) (jhoward)
 *    - Add custom styles in <menu-switcher> for CHI menus.
 *
 *  2024-07-09 (1.2.0) (jhoward)
 *    - Add isDockLeft prop in <menu-switcher>.
 *    - Add accessibility-related attributes in <menu-switcher>.
 *
 *  2024-07-10 (1.2.1) (jhoward)
 *    - Modify menu button styles in <menu-switcher>.
 *    - Improve menu button placement in <menu-switcher>.
 *
 *  2024-07-19 (1.2.2) (jhoward)
 *    - Do not render <menu-switcher> if no related menus exist.
 *
 *  2024-12-03 (1.2.3) (jhoward)
 *    - Fix <menu-switcher> displaying over content when closed.
 *
 *  2024-12-03 (1.2.4) (jhoward)
 *    - Change <menu-switcher> layout, giving each menu button more space and breaking on words if words are too long.
 *
 *  2025-01-24 (1.2.5) (jhoward)
 *    - Hide menu icons from screen readers in <menu-switcher>.
 *
 *  2025-04-08 (1.3.0) (jhoward)
 *    - Hide menu icons and do not hide menu titles when scrolling in <menu-switcher>.
 *
 *  2025-05-09 (1.4.0) (jhoward)
 *    - Respect new menu visibility override in <menu-switcher>.
 *    - Add isIconsVisible prop in <menu-switcher>.
 *    - Rename isIconVisible computed property to isIconsDisplayed and consider isIconsVisible prop in isIconsDisplayed() in <menu-switcher>.
 */

import {defineComponent, PropType} from "vue";
import {Menu, VisualMenuComponent, TextualMenuComponent, MenuSwitcherComponent} from "./defs";
import {methodIsModelScheduled} from "../../helpers/menu-sections";

export default defineComponent({
  name: 'menu-switcher',
  emits: {
    opened: () => true,
    closed: () => true,
  },
  props: {
    menus: {
      type: Array as PropType<Menu[]>,
      required: true
    },
    currentMenuId: {
      type: Number,
      default: 0
    },
    deviceAssetCode: {
      type: String,
      default: ''
    },
    isCollapsable: {
      type: Boolean,
      default: false
    },
    isDockLeft: {
      type: Boolean,
      default: false
    },
    isIconsVisible: {
      type: Boolean,
      default: false
    },
  },
  mounted: function () {
    document.addEventListener('click', () => {
      if (this.isCollapsableOrMobileDockLeft) {
        this.isOpen = false;
      }
    });
    if (!this.isDockLeft) {
      document.addEventListener('scroll', () => {
        this.windowScrollY = window.scrollY;
      }, {passive: true});
    }

    window.addEventListener('resize', () => {
      window.clearTimeout(this.windowWidthTimer);
      this.windowWidthTimer = window.setTimeout(() => {
        this.windowWidth = window.innerWidth;

        if (this.isDockLeft) {
          this.setDockBottom();
        }
      }, 128);
    }, {passive: true});

    window.setTimeout(this.setDockBottom, 1024);
  },
  data: function () {
    return {
      isOpen: false,
      maxHeight: 128,
      minHeight: 72,
      maxFontSize: 36,
      minFontSize: 0,
      maxVerticalPadding: 16,
      minVerticalPadding: 8,
      switcherHeight: 128,

      percentScroll: 0,
      windowScrollY: 0,

      windowWidthTimer: -1,
      windowWidth: window.innerWidth,

      menuSectionContainer: null as HTMLDivElement | null,
      dockLeftBottom:  80,
    };
  },
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.$emit('opened');
      } else {
        this.$emit('closed');
      }
    }
  },
  computed: {
    isIconsDisplayed() {
      return this.isIconsVisible && (this.isDockLeft || 1 > this.percentScroll);
    },
    containerStyles(): { [key: string]: number | string } {
      this.switcherHeight = Math.min(this.maxHeight, Math.max(this.minHeight, this.maxHeight - this.windowScrollY));
      this.percentScroll = Math.min(1, Math.max(0, this.windowScrollY / (this.maxHeight - this.minHeight)));
      return {
        '--menu-count': this.visibleMenus.length,
        '--switcher-height': this.switcherHeight + 'px',
        '--switcher-icon-font-size': this.minFontSize + ((this.maxFontSize - this.minFontSize) * (1 - this.percentScroll)) + 'px',
        '--switcher-vertical-padding': this.minVerticalPadding + ((this.maxVerticalPadding - this.minVerticalPadding) * (1 - this.percentScroll)) + 'px',
        '--switcher-dock-left-bottom': this.dockLeftBottom + 'px',
      };
    },
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu_switcher--container-open': this.isOpen,
        'menu_switcher--container-collapsable': this.isCollapsableOrMobileDockLeft,
        'menu_switcher--container-dock_left': this.isDockLeft,
        'menu_switcher--container-is_icon_visible': this.isIconsDisplayed,
      };
    },

    isMobileUi(): boolean {
      return this.windowWidth < 800;
    },

    isCollapsableOrMobileDockLeft(): boolean {
      return this.isCollapsable || (this.isDockLeft && this.isMobileUi);
    },

    menuPageTitle(): string {
      return this.visibleMenus[0]?.page?.page_h1 || '';
    },

    visibleMenus(): Menu[] {
      return Array.from(this.menus.filter((menu) => {
        return this.$store.state.optionAllowListHiddenMenuIds.includes(menu.menu_id) || (this as unknown as MenuSwitcherComponent).isModelScheduled(menu.normalizedSchedules || []);
      }));
    },
  },
  methods: {
    setDockBottom() {
      if (!this.menuSectionContainer) {
        this.menuSectionContainer = document.querySelector('.menu--section_nav') as HTMLDivElement | null;
      }

      if (this.menuSectionContainer && this.isDockLeft) {
        this.dockLeftBottom = this.menuSectionContainer.getBoundingClientRect().height;
      }
    },
    getMenuTitle(menu: Menu): string {
      return menu.menu_type?.mt_title || menu.full_title || 'Menu';
    },

    switchMenus(menuId: number) {
      (this.$parent as VisualMenuComponent | TextualMenuComponent).switchToRelatedMenu(menuId);
    },

    isModelScheduled: methodIsModelScheduled,
  }
})
